import "./style.scss";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import type { AutocompleteRenderInputParams } from "@mui/material";
import { CircularProgress } from "@mui/material";
import CustomFieldsCollapse from "components/shared/CustomFieldsCollapse";
import MessageBlock from "components/shared/MessageBlock";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import InfoTooltip from "components/shared/InfoTooltip";
import InlineButton from "components/shared/InlineButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import TitleCover from "components/shared/TitleCover";
import UploadImg from "components/shared/UploadImg";
import { onHighlightField } from "components/TitleFormContainer/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IWork } from "containers/Works/types";
import type { ICustomField } from "store/customFieldsStore";
import { fetchTitleFormatTypes } from "store/titleFormatTypeStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { sendPicture } from "store/titleStore";
import type { TFetchStatus } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import { useCompanySettings } from "../../../hooks/useCompanySettings";
import NameTranslationWrapper from "../../shared/NameTranslationWrapper";
import type { TEan13DilicomOption, TInitialHighlightField, TStatuses } from "../types";
import WorkSelectField from "../WorkSelectField";

interface IGeneralInfoStep {
    /**
     * React useForm() hooks
     */
    useFormData: any;
    mode: "new" | "edit-page";
    editModeActive?: boolean;
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    /**
     * Set ean13 value
     */
    setEan13Value: React.Dispatch<React.SetStateAction<string>>;
    /**
     * Current value of field ean13
     */
    ean13Value: string;
    /**
     * Here used to reset fied ean13 options
     */
    setEan13DilicomOption: React.Dispatch<React.SetStateAction<TEan13DilicomOption>>;
    /**
     * Options for the Text/Autocomplete field ean13, while finding a Title on Dilicom servers
     */
    ean13DilicomOption: TEan13DilicomOption;
    /**
     * Hightlight field condition
     */
    highlightField: TInitialHighlightField;
    /**
     * Open the Dilicom informations modal
     */
    setOpenInfoModal: React.Dispatch<React.SetStateAction<boolean>>;
    /**
     * Reset highlight fields style while clicking on it after Dilicom autocomplete
     * */
    onResetHighlight: (fieldName: string) => void;
    /**
     * Title statuses uses for upload image field
     * */
    statuses: TStatuses;
    customFields: ICustomField[];
    /**
     * Check if form has draft ean13
     */
    hasDraftEAN13?: boolean;
    dilicomFetchStatus: TFetchStatus;
    workField?: "select" | "locked" | "hidden";
    workExtraOption?: IWork | null;
    isBookPortal?: boolean;
}

const GeneralInfoStep = ({
    useFormData,
    mode,
    editModeActive,
    stepTitle,
    stepSubtitle,
    indexStep,
    ean13Value,
    setEan13Value,
    setEan13DilicomOption,
    ean13DilicomOption,
    highlightField,
    setOpenInfoModal,
    onResetHighlight,
    statuses,
    customFields,
    hasDraftEAN13,
    dilicomFetchStatus,
    workField = "hidden",
    workExtraOption = null,
    isBookPortal = false,
}: IGeneralInfoStep) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { control, errors, watch, setValue } = useFormData;
    const formatTypes = useAppSelector((state) => state.titleFormatTypes.items);
    const { countryFeatures } = useCompanySettings();

    const [open, setOpen] = useState(false);

    // Filter fields that should not appear on creation :
    const customFieldsOnCreation = customFields.filter((field) => field.showOnCreation);

    const currentAvatar: string = watch("avatarUrl");
    const showArtworkField = countryFeatures?.urssaf === true;
    const selectedWorkId = watch("work")?.id ?? null;
    const originalWorkId = workExtraOption?.id ?? null;
    const hasChangedWork =
        mode === "edit-page" && selectedWorkId != null && originalWorkId != null && selectedWorkId !== originalWorkId;
    const selectedFormatTypeId = watch("formatType")?.id ?? null;
    const originalFormatTypeIdRef = useRef(selectedFormatTypeId);
    const hasChangedFormatType = mode === "edit-page" && originalFormatTypeIdRef.current !== selectedFormatTypeId;

    /**
     * Check if ean13 contains only digits (needs to be true for Dilicom)
     */
    const checkEan13 = (value: string) => {
        return /^\d+$/.test(value);
    };

    useEffect(() => {
        const fetchAvatar = async (fileList: FileList) => {
            if (!fileList || fileList?.length === 0) return;
            dispatch(sendPicture(fileList[0])).then((res: any) => {
                if (res.meta.requestStatus === "fulfilled") {
                    setValue("avatarUrl", res.payload as string);
                }
                if (res.meta.requestStatus === "rejected") {
                    setValue("avatarUrl", "");
                }
            });
        };
        if (typeof currentAvatar !== "string") {
            fetchAvatar(currentAvatar);
        }
    }, [dispatch, setValue, currentAvatar]);

    useEffect(() => {
        if (isBookPortal) {
            dispatch(fetchTitleFormatTypes());
        }
    }, [dispatch, isBookPortal]);

    /** Reset autocomplete field options on modify input if the value doesn't match the registered ean13 Dilicom result */
    const onModifyEan13Input = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const inputValue = e.target.value;
        setValue("ean13", inputValue, { shouldDirty: true });
        await setEan13Value(inputValue);

        // If inputValue is 13 characters long, open the options:
        if (inputValue.length === 13 && ean13DilicomOption && checkEan13(inputValue)) {
            setOpen(true);
        }
        if (ean13DilicomOption.length && ean13DilicomOption[0].ean13 !== inputValue) {
            setEan13DilicomOption([]);
        }
    };

    const renderInputEan13 = (params: AutocompleteRenderInputParams) => {
        /** Because this field is used as an autocomplete and a textfield at the same time to handle Dilicom informations
         * I used the onChange event and onKeydown to avoid some reactions of the error useForm
         */
        return (
            <StyledTextField
                {...params}
                error={!!errors.ean13}
                helperText={errors.ean13?.message ?? ""}
                onChange={(e) => onModifyEan13Input(e)}
                onKeyDown={(e) => e.code === "Enter" && setValue("ean13", ean13Value)}
                onBlur={(e) => setValue("ean13", e.target.value)}
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                        <>
                            {dilicomFetchStatus === "pending" && (
                                <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />
                            )}
                        </>
                    ),
                }}
            />
        );
    };

    const renderEan13Options = (props: object, option: { ean13: string; imgUrl: string; title: string }) => {
        return (
            ean13DilicomOption[0].title && (
                <div {...props} className="dilicom-result" onClick={() => setOpenInfoModal(true)}>
                    <div className="dilicom-option-left">
                        <TitleCover
                            coverUrl={option.imgUrl}
                            title={option.title?.slice(0, 1).toUpperCase()}
                            style={{ width: 30 }}
                        />
                        <div className="separator" />
                        <span>{option.title}</span>
                    </div>
                    <div className="dilicom-option-right">
                        <AutoFixHighIcon sx={{ width: "15px", height: "15px", color: "#200b73" }} />
                        <span>{t("NewTitle.ExhaustiveBookFile")}</span>
                    </div>
                </div>
            )
        );
    };

    // Closes autocomplete options if value in ean13 field shorter than 13 characters.
    useEffect(() => {
        if (ean13Value.length !== 13 || !checkEan13(ean13Value)) {
            setOpen(false);
        }
    }, [ean13Value]);

    /**
     * Is fetch of Dilicom data pending.
     */
    const fetchingDilicomData = useMemo(() => {
        return ean13Value.length === 13 && ["idle", "pending"].includes(dilicomFetchStatus);
    }, [ean13Value, dilicomFetchStatus]);

    return (
        <div className="title-general-info-step">
            <h3 className="form-section-title">
                {" "}
                {indexStep + 1}. {stepTitle}{" "}
                {mode === "edit-page" && (
                    <InfoTooltip
                        text={t("TransferTerms.Helpdesk.AccessDoc")}
                        link={"https://helpdesk.crealo.app/fr/article/modifier-un-titre-t37o3j/"}
                        iconType="question"
                    />
                )}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>

            <div className="form-fields">
                {workField === "select" && (
                    <>
                        {mode !== "edit-page" && (
                            <MessageBlock type="info" small text={t("NewTitle.WorkOptionalHint")} />
                        )}
                        <WorkSelectField
                            control={control}
                            error={errors.work}
                            required={mode === "edit-page"}
                            allowCreate
                            extraOption={workExtraOption}
                            suggestedName={watch("name")}
                            onSelectWork={(work) => setValue("work", work)}
                        />
                        {hasChangedWork && <MessageBlock type="info" small text={t("NewTitle.WorkMoveHint")} />}
                    </>
                )}
                {workField === "locked" && (
                    <TextFormComponent
                        label={t("Works.WorkLabel")}
                        control={control}
                        controlName="workDisplayName"
                        disabled
                    />
                )}
                {/** UPPER IMAGE SECTION IF EDIT TITLE */}
                {mode === "edit-page" && (
                    <Controller
                        name="avatarUrl"
                        control={control}
                        render={({ field }) => (
                            <div className="add-image-section">
                                <div className="image-button-wrapper">
                                    <UploadImg
                                        {...field}
                                        watchAvatar={currentAvatar}
                                        editModeActive={editModeActive}
                                        mode={mode}
                                    />
                                    {currentAvatar !== "" && (
                                        <LoadingButton
                                            loading={statuses?.sendPictureStatus === "pending"}
                                            onClick={() => setValue("avatarUrl", "")}
                                            sx={{
                                                height: "fit-content",
                                                color: "black",
                                            }}
                                        >
                                            {statuses?.sendPictureStatus !== "pending" && (
                                                <RefreshIcon sx={{ color: "black" }} />
                                            )}
                                        </LoadingButton>
                                    )}
                                </div>
                            </div>
                        )}
                    />
                )}
                <AutocompleteFormComponent
                    label={t("NewTitle.ISBN")}
                    control={control}
                    controlName="ean13"
                    options={ean13DilicomOption}
                    renderInput={renderInputEan13}
                    renderOption={renderEan13Options}
                    renderOptionText={(option: any) => `${option.ean13 || ean13Value}`}
                    disableClearable
                    required={!hasDraftEAN13}
                    noOptionsText={t("NewTitle.NoDilicomOption")}
                    open={open}
                    onClose={() => setOpen(false)}
                    loading={fetchingDilicomData}
                />
                <TextFormComponent label={t("Titles.SKU")} control={control} controlName="sku" error={errors.sku} />

                <div className="title-and-release-row">
                    <DateFormComponent
                        label={t("NewTitle.ReleaseDate")}
                        control={control}
                        controlName="releaseDate"
                        sx={onHighlightField(highlightField.releaseDate)}
                        onFocus={() => onResetHighlight("releaseDate")}
                        error={errors.releaseDate}
                        maxYearDistance={10}
                    />
                    {isBookPortal ? (
                        <div className="name-field-wrapper">
                            <AutocompleteFormComponent
                                label={t("NewTitle.Format")}
                                placeholder={t("NewTitle.Format")}
                                control={control}
                                controlName="formatType"
                                options={formatTypes}
                                renderOptionText={(option: { name?: string }) => option?.name ?? ""}
                                isOptionEqualToValue={(option: { id: number }, value: { id: number }) =>
                                    option?.id === value?.id
                                }
                                error={errors.formatType}
                            />
                            {hasChangedFormatType && (
                                <MessageBlock type="info" small text={t("NewTitle.FormatTypeChangeHint")} />
                            )}
                        </div>
                    ) : (
                        <div className="name-field-wrapper">
                            <TextFormComponent
                                label={t("NewTitle.Title")}
                                fullWidth
                                control={control}
                                controlName="name"
                                placeholder={t("NewTitle.TitleHere")}
                                sx={onHighlightField(highlightField.name)}
                                error={errors.name}
                                onFocus={() => onResetHighlight("name")}
                                required
                            />
                            <NameTranslationWrapper
                                control={control}
                                originalName={watch("name")}
                                nameLabel={t("NewTitle.Title")}
                            />
                        </div>
                    )}
                </div>
                {isBookPortal && (
                    <div className="name-field-wrapper">
                        <TextFormComponent
                            label={t("NewTitle.Title")}
                            fullWidth
                            control={control}
                            controlName="name"
                            placeholder={t("NewTitle.TitleHere")}
                            sx={onHighlightField(highlightField.name)}
                            error={errors.name}
                            onFocus={() => onResetHighlight("name")}
                            required
                        />
                        <NameTranslationWrapper
                            control={control}
                            originalName={watch("name")}
                            nameLabel={t("NewTitle.Title")}
                        />
                    </div>
                )}

                {/** IMAGE SECTION DOWN ON CREATE TITLE */}
                {showArtworkField && mode === "new" && (
                    <Controller
                        name="avatarUrl"
                        control={control}
                        render={({ field }) => (
                            <div className="add-image-section">
                                <label className="form-label">{t("NewTitle.ArtworkImage")}</label>
                                <div className="image-button-wrapper">
                                    <UploadImg
                                        {...field}
                                        watchAvatar={currentAvatar}
                                        mode={mode}
                                        editModeActive={editModeActive}
                                    />
                                    {currentAvatar !== "" && (
                                        <LoadingButton
                                            loading={statuses?.sendPictureStatus === "pending"}
                                            onClick={() => setValue("avatarUrl", "")}
                                            sx={{
                                                height: "fit-content",
                                                color: "black",
                                            }}
                                        >
                                            {statuses?.sendPictureStatus !== "pending" && (
                                                <RefreshIcon sx={{ color: "black" }} />
                                            )}
                                        </LoadingButton>
                                    )}
                                </div>
                            </div>
                        )}
                    />
                )}

                {/** COLLAPSE FOR THE CUSTOM FIELDS */}
                {customFields.length > 0 && (
                    <CustomFieldsCollapse
                        sectionFields={mode === "new" ? customFieldsOnCreation : customFields}
                        errors={errors}
                        mode={mode}
                    />
                )}
            </div>
        </div>
    );
};

export default GeneralInfoStep;
